// Multi-city configuration seam (FRONTEND / build-time).
//
// One codebase builds two sites: DC (default) and Arlington, selected by the
// build-time `VITE_CITY` env var (`vite build` / vitest read it from the shell;
// see vite.config.ts for the matching index.html <head> transform, and
// scripts/seed.ts + scripts/*.sh for the runtime `CITY` seam on the backend).
//
//   VITE_CITY unset | "dc"  → DC   (prefix pool-tracker, pools.districtjosh.com)
//   VITE_CITY = "arlington" → Arlington spraygrounds (arlsplash.districtjosh.com)
//
// DC IS LIVE: the `DC` config below must reproduce every user-facing string and
// link byte-for-byte so the default build and the existing test-suite stay green.
// Arlington is spraygrounds-only — the pool/kiddie/slide machinery stays in the
// code but is dormant (`features.pools/slides = false`); every Arlington entry is
// a `kind:"splash"` Pool reported on its `main` section.

import type { LatLngTuple } from "leaflet";
import type { SplashPad } from "../shared/types";
import { DC_BOUNDARY } from "./dc-boundary";
import { ARLINGTON_BOUNDARY } from "./arlington-boundary";
import { SPLASH_PADS, SPLASH_SEASON } from "./splash-pads";
import { splashSeasonLine } from "./splash-season";
import { ARLINGTON_SPRAYGROUNDS, ARLINGTON_SPLASH_SEASON } from "../shared/arlington-spraygrounds";

export type CityId = "dc" | "arlington";

/** Which top-level surfaces a city exposes. Arlington = spraygrounds only. */
export interface CityFeatures {
  /** The kiddie-focused "Pools" tab/list (and its default home route). */
  pools: boolean;
  /** Water-slide report UI on pool cards/detail. */
  slides: boolean;
  /** The Splash tab. */
  splash: boolean;
}

export interface CityAbout {
  heading: string;
  /** Inner HTML for the intro paragraph (already trusted, not user input). */
  intro: string;
  email: string;
  xUrl: string;
  xHandle: string;
  bskyUrl: string;
  bskyHandle: string;
}

export interface CityConfig {
  id: CityId;
  /** Brand shown as the <h1> across the views (and the OG site name). */
  displayName: string;
  features: CityFeatures;

  // --- Map ---
  boundary: LatLngTuple[];
  /** [lat, lng] map center used when fitBounds has nothing to work with. */
  mapCenter: [number, number];
  mapZoom: number;

  // --- List (pools) view copy ---
  listTagline: string;
  listSubhead: string;

  // --- Splash view ---
  /** Label for the splash tab ("Splash Pads" vs "Spraygrounds"). */
  splashTabLabel: string;
  splashTagline: string;
  /**
   * The intro paragraph above the pad list. A FUNCTION of the current time
   * because DC's schedule sentence changes with the season (see
   * `splashSeasonLine`) — a fixed string went stale mid-summer. Cities without a
   * phased season just ignore the argument.
   */
  splashIntro: (now?: Date) => string;
  /** The catalog the splash view renders (adapted to the SplashPad shape). */
  splashCatalog: SplashPad[];
  splashSeasonNote: string;
  /** DC groups pads by ward; Arlington has no wards → show `neighborhood`. */
  showWard: boolean;

  // --- Branding / links ---
  donateUrl: string;
  /** Word used in the About "← Back to <x>" link. */
  backToLabel: string;

  // --- About page ---
  about: CityAbout;

  // --- Footer ---
  footNote: string;
  statusUrl: string;
  statusLinkLabel: string;
  statusCaveat: string;
  /**
   * The footer explainer used on SPLASH surfaces (the Splash tab and splash
   * detail pages). `statusCaveat` is about children's-section status, which
   * says nothing about a spray pad — see `footerHtml("splash")`.
   */
  splashStatusCaveat: string;

  // --- Address display ---
  /** Trailing ", <City>, <ST> <zip>" suffix stripped by `displayAddress`. */
  addressSuffixPattern: RegExp;

  // --- Namespacing / observability ---
  /** localStorage key prefix (favorites, deviceId) — keeps the two sites apart. */
  localStoragePrefix: string;
  /** Datadog RUM `service` tag (shared districtjosh RUM app, sliced by service). */
  rumService: string;
}

const DC: CityConfig = {
  id: "dc",
  displayName: "DC Children's Pool Tracker",
  features: { pools: true, slides: true, splash: true },

  boundary: DC_BOUNDARY,
  mapCenter: [38.9072, -77.0369],
  mapZoom: 12,

  listTagline: "Is the children's pool actually open right now?",
  listSubhead:
    "Crowd-reported status for DC's children's pool sections, which can close on short staffing without notice.",

  splashTabLabel: "Splash Pads",
  splashTagline: "User reported splash pad availability",
  splashIntro: (now?: Date) =>
    `Tap a splash pad to see the specific schedule. ${splashSeasonLine(now)}`,
  splashCatalog: SPLASH_PADS,
  splashSeasonNote: SPLASH_SEASON.notes,
  showWard: true,

  donateUrl: "http://districtjosh.com/donate",
  backToLabel: "pools",

  about: {
    heading: "About DC Pool Tracker",
    intro:
      "DC Pool Tracker crowdsources the real-time open/closed status for DC children's pools. DC public pools close their children's sections without notice when lifeguards aren't available. DPR's official hours don't reflect this. This app lets swimmers report what they actually find on the ground so other families can plan ahead.",
    email: "joshjacobsondc@gmail.com",
    xUrl: "https://x.com/joshjacobsondc",
    xHandle: "@joshjacobsondc",
    bskyUrl: "https://bsky.app/profile/vegjosh.bsky.social",
    bskyHandle: "@vegjosh.bsky.social",
  },

  footNote:
    "Crowd-reported. Scheduled hours from DC DPR. Reports cross-checked against published hours.",
  statusUrl: "https://dprsplash.splashthat.com/",
  statusLinkLabel: "Check DPR's real-time pool status ↗",
  statusCaveat:
    "DPR's status page covers whole-facility open/closed only; it does not report children's section status, which is what this app crowdsources.",
  splashStatusCaveat:
    "Spray parks can also be off for repairs, or during the weekly maintenance windows noted on each listing, which is what this app crowdsources.",

  addressSuffixPattern: /,\s*Washington,\s*D\.?C\.?(?:\s+\d{5}(?:-\d{4})?)?\s*$/i,

  localStoragePrefix: "pool-tracker",
  rumService: "pool-tracker",
};

// Arlington spraygrounds → SplashPad shape the splash view consumes. Arlington
// has no wards, so `ward` is a dormant 0 and the view renders `neighborhood`
// instead (showWard:false); the per-card schedule text comes from `scheduleNote`
// (the spraygrounds run different hours each weekday) rather than DC's
// `maintenanceWindow`. Mosaic (closedForSeason) keeps its note so the card still
// communicates the closure.
const ARLINGTON_SPLASH_CATALOG: SplashPad[] = ARLINGTON_SPRAYGROUNDS.map((s) => ({
  id: s.id,
  name: s.name,
  address: s.address,
  ward: 0,
  lat: s.lat,
  lng: s.lng,
  neighborhood: s.neighborhood,
  scheduleNote: s.scheduleNote,
  confidence: s.confidence,
}));

const ARLINGTON: CityConfig = {
  id: "arlington",
  displayName: "Arlington Sprayground Tracker",
  features: { pools: false, slides: false, splash: true },

  boundary: ARLINGTON_BOUNDARY,
  mapCenter: [38.878, -77.1],
  mapZoom: 13,

  // Pools surfaces are hidden for Arlington, but keep sensible copy in case the
  // route is reached directly.
  listTagline: "Is the sprayground on right now?",
  listSubhead: "Crowd-reported status for Arlington County spraygrounds.",

  splashTabLabel: "Spraygrounds",
  splashTagline: "User reported sprayground availability",
  // Arlington runs one continuous season (no weekends-only shoulder), so its
  // intro is static — only "Click" becomes "Tap" for touch parity with DC.
  splashIntro: () =>
    "Tap a sprayground to see its weekly schedule. Arlington's free spraygrounds run different hours each weekday, roughly Memorial Day weekend through Labor Day weekend.",
  splashCatalog: ARLINGTON_SPLASH_CATALOG,
  splashSeasonNote: ARLINGTON_SPLASH_SEASON.notes,
  showWard: false,

  donateUrl: "http://districtjosh.com/donate",
  backToLabel: "spraygrounds",

  about: {
    heading: "About Arlington Sprayground Tracker",
    intro:
      "Arlington Sprayground Tracker crowdsources the real-time on/off status for Arlington County's free spraygrounds. Posted hours vary by day and don't always reflect what's actually running. This app lets families report what they find on the ground so others can plan ahead.",
    email: "joshjacobsondc@gmail.com",
    xUrl: "https://x.com/joshjacobsondc",
    xHandle: "@joshjacobsondc",
    bskyUrl: "https://bsky.app/profile/vegjosh.bsky.social",
    bskyHandle: "@vegjosh.bsky.social",
  },

  footNote:
    "Crowd-reported. Scheduled hours from Arlington Parks & Recreation. Reports cross-checked against published hours.",
  statusUrl:
    "https://www.arlingtonva.us/Government/Programs/Parks-Recreation/Parks/Pools-Spray-Parks",
  statusLinkLabel: "Arlington's official spraygrounds page ↗",
  statusCaveat: "",
  splashStatusCaveat:
    "Posted hours vary by day and don't always reflect what's actually running, which is what this app crowdsources.",

  addressSuffixPattern: /,\s*Arlington,\s*VA(?:\s+\d{5}(?:-\d{4})?)?\s*$/i,

  localStoragePrefix: "arlsplash",
  rumService: "arlsplash",
};

const CITY_ID = ((import.meta.env.VITE_CITY as string | undefined) ?? "dc").toLowerCase();

/** The resolved city config for this build. Defaults to DC. */
export const CITY: CityConfig = CITY_ID === "arlington" ? ARLINGTON : DC;
